import { useEffect, useState } from "react";
import { Lock, PencilLine, Presentation, Vote, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SlidesPanel } from "@/components/meeting/SlidesPanel";
import { WhiteboardPanel } from "@/components/meeting/Whiteboard";
import { PollsPanel } from "@/components/meeting/PollsPanel";
import { MEETING_TYPES, fmtMeetingDate } from "@/components/meeting/meetUtils";

const tabCls = "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2";

export default function MeetingToolPage() {
  const [type, setType] = useState("monday");
  const [sub, setSub] = useState("slides");
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/meeting/meta").then(({ data }) => setMeta(data)).catch(() => setMeta(null));
  }, []);

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid="meeting-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow flex items-center gap-2">
            <Users className="h-3.5 w-3.5" /> Ruang meeting tim
            <Badge variant="outline" className="border-[color:var(--hairline)] text-[10px] text-primary ml-1"><Lock className="h-3 w-3 mr-1" /> Admin only</Badge>
          </div>
          <h1 className="font-display text-4xl mt-1.5" data-testid="meeting-title">Meeting Eddy</h1>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-3xl leading-relaxed">
            Slide briefing dengan gambar AI, papan tulis untuk memetakan ide, dan voting yang bisa dibagikan lewat link publik.
          </p>
        </div>
      </div>

      {/* Main menu */}
      <div className="grid sm:grid-cols-2 gap-3" role="tablist" aria-label="Jenis meeting" data-testid="meeting-type-menu">
        {MEETING_TYPES.map((m) => {
          const on = type === m.value;
          return (
            <button
              key={m.value}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setType(m.value)}
              className={`press text-left rounded-xl border px-5 py-4 transition-colors ${on ? "border-primary bg-[hsl(43_30%_9%)]" : "border-border bg-card hover:border-[hsl(43_35%_35%)]"}`}
              data-testid={`meeting-type-${m.value}`}
            >
              <div className={`font-display text-2xl ${on ? "text-primary" : ""}`}>{m.label}</div>
              <div className="text-xs text-muted-foreground mt-1">
                Setiap {m.day} · berikutnya {meta ? fmtMeetingDate(meta.next[m.value]) : "…"}
              </div>
            </button>
          );
        })}
      </div>

      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="bg-[hsl(220_12%_8%)] border border-border h-auto p-1 flex-wrap" data-testid="meeting-subtabs">
          <TabsTrigger value="slides" className={tabCls} data-testid="meeting-tab-slides"><Presentation className="h-4 w-4 mr-1.5" /> Slide</TabsTrigger>
          <TabsTrigger value="board" className={tabCls} data-testid="meeting-tab-board"><PencilLine className="h-4 w-4 mr-1.5" /> Papan Tulis</TabsTrigger>
          <TabsTrigger value="polls" className={tabCls} data-testid="meeting-tab-polls"><Vote className="h-4 w-4 mr-1.5" /> Voting</TabsTrigger>
        </TabsList>
        <TabsContent value="slides" className="mt-5 focus-visible:ring-0 focus-visible:ring-offset-0">
          <SlidesPanel key={`s-${type}`} type={type} nextDate={meta?.next?.[type]} />
        </TabsContent>
        <TabsContent value="board" className="mt-5 focus-visible:ring-0 focus-visible:ring-offset-0">
          <WhiteboardPanel key={`b-${type}`} type={type} />
        </TabsContent>
        <TabsContent value="polls" className="mt-5 focus-visible:ring-0 focus-visible:ring-offset-0">
          <PollsPanel key={`p-${type}`} type={type} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
